import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Subscribe() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const cancelled = new URLSearchParams(loc.search).get("cancel") === "1";

  useEffect(() => {
    axios
      .get(`${API}/subscriptions/plans`)
      .then((r) => setPlans(r.data.plans || []))
      .catch(() => setPlans([]));
  }, []);

  const subscribe = async (lookup_key) => {
    if (!user) {
      nav("/login", { state: { returnTo: "/subscribe" } });
      return;
    }
    setBusy(lookup_key);
    try {
      const { data } = await axios.post(`${API}/subscriptions/checkout`, {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start subscription");
      setBusy("");
    }
  };

  return (
    <div
      data-testid="subscribe-page"
      className="min-h-screen bg-[color:var(--sr-bg)] pt-32 md:pt-40 pb-24"
    >
      <div className="sr-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="max-w-3xl mb-16"
        >
          <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> The Ritual
          </p>
          <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight text-[color:var(--sr-primary)]">
            A quiet six,
            <br />
            <em className="italic font-light text-[color:var(--sr-accent)]">
              on repeat.
            </em>
          </h1>
          <p className="mt-8 text-[color:var(--sr-text)]/75 leading-relaxed max-w-xl">
            A curated six-pack — chosen by us, tuned to the season — delivered
            weekly or monthly. Pause anytime. Cancel anytime.
          </p>
        </motion.div>

        {cancelled && (
          <p
            data-testid="sub-cancelled"
            className="mb-8 text-xs uppercase tracking-[0.28em] text-[color:var(--sr-muted)]"
          >
            No worries — nothing was charged.
          </p>
        )}

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10"
          data-testid="subscribe-plans"
        >
          {plans.map((p, idx) => (
            <motion.div
              key={p.lookup_key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 + idx * 0.1 }}
              className="border hairline p-8 md:p-12 bg-[color:var(--sr-secondary)] flex flex-col"
              data-testid={`plan-${p.lookup_key}`}
            >
              <p className="label-eyebrow text-[color:var(--sr-accent)] mb-4">
                {p.cadence}
              </p>
              <h3 className="font-serif text-4xl md:text-5xl text-[color:var(--sr-primary)] leading-tight tracking-tight">
                {p.name.split(" · ")[1] || p.name}
              </h3>
              <p className="mt-6 text-sm text-[color:var(--sr-text)]/75 leading-relaxed">
                {p.description}
              </p>

              <ul className="mt-6 space-y-3 text-sm text-[color:var(--sr-primary)]">
                <PerkLine>Six bottles, curated by us</PerkLine>
                <PerkLine>{p.savings}</PerkLine>
                <PerkLine>Pause or cancel anytime</PerkLine>
                <PerkLine>Free shipping, always</PerkLine>
              </ul>

              <div className="mt-10 pt-8 border-t hairline-light flex items-baseline gap-2">
                <span className="font-serif text-6xl text-[color:var(--sr-primary)] tabular-nums">
                  ${(p.amount / 100).toFixed(2)}
                </span>
                <span className="label-eyebrow text-[color:var(--sr-muted)]">
                  / {p.interval}
                </span>
              </div>

              <button
                type="button"
                onClick={() => subscribe(p.lookup_key)}
                disabled={!!busy}
                data-testid={`subscribe-btn-${p.lookup_key}`}
                className="mt-8 inline-flex items-center justify-between px-6 py-4 min-h-[44px] bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors disabled:opacity-40"
              >
                <span>
                  {busy === p.lookup_key ? "Redirecting…" : "Begin the ritual"}
                </span>
                <ArrowRight size={16} strokeWidth={1.25} />
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-12 text-xs text-[color:var(--sr-muted)] max-w-2xl leading-relaxed">
          By subscribing you agree to recurring charges until cancelled. Manage
          or cancel your subscription anytime from{" "}
          <Link
            to="/account"
            className="text-[color:var(--sr-primary)] underline decoration-[color:var(--sr-accent)] underline-offset-4"
          >
            your account
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function PerkLine({ children }) {
  return (
    <li className="flex items-center gap-3">
      <Check size={14} strokeWidth={1.5} className="text-[color:var(--sr-accent)]" />
      <span>{children}</span>
    </li>
  );
}
